import { PortalConfigurationType, UserProfile, VendorContactRole, VendorProfile } from '@trnsact/trnsact-shared-types';
import { usePortalConfigurationChecks } from 'modules/portalConfigurations';
import { useSelector } from 'react-redux';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { PortalConfigurationRolesSettingsKeys } from 'types';

export function useRoutesVisibilityChecks() {
  const portalConfigurationData = useSelector((state: any) => portalConfigurationsSelectors.configData(state));

  const { checkPortalConfigEntryForCurrentUserRole, checkAftermarketPartnerAllowSendProposals } =
    usePortalConfigurationChecks();

  const byUserRole = (userProfile: UserProfile, userRole: VendorContactRole) =>
    userProfile.vendorContactRole === userRole;

  const checkIfOpportunitiesVisible = (
    isAftermarketPartner: boolean,
    isDealerUser: boolean,
    isLenderUser: boolean,
    vendorProfile: VendorProfile
  ) => {
    if (isAftermarketPartner) {
      return checkAftermarketPartnerAllowSendProposals();
    }
    if (isDealerUser) {
      return !checkPortalConfigEntryForCurrentUserRole(
        PortalConfigurationType.Opportunities,
        PortalConfigurationRolesSettingsKeys.Hidden
      );
    }
    if (isLenderUser) {
      return !!vendorProfile;
    }
    return false;
  };

  const checkIfSubmissionsHubIsVisible = (isDealerUser: boolean, isSuperAdminOrSingleAccount: boolean) => {
    if (isSuperAdminOrSingleAccount) {
      return true;
    }

    if (isDealerUser) {
      return !checkPortalConfigEntryForCurrentUserRole(
        PortalConfigurationType.SubmissionsHubManagement,
        PortalConfigurationRolesSettingsKeys.Hidden
      );
    }

    return false;
  };

  const checkIfAftermarketVisible = (
    isDealerUser: boolean,
    isCommonPartner: boolean,
    isAftermarketPartner: boolean,
    isSuperAdminOrSingleAccount: boolean
  ) => {
    if (isAftermarketPartner) return true;

    if (isCommonPartner) return false;

    if (isDealerUser) {
      const portalConfiguration = portalConfigurationData?.[PortalConfigurationType.Aftermarket];

      if (portalConfiguration) {
        return (
          isSuperAdminOrSingleAccount ||
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Aftermarket,
            PortalConfigurationRolesSettingsKeys.AssignedOnly
          ) ||
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Aftermarket,
            PortalConfigurationRolesSettingsKeys.ReadOnly
          )
        );
      }

      return true;
    }

    return false;
  };

  const checkIfInventoryIsVisible = (isDealerUser: boolean) => {
    if (isDealerUser) {
      const portalConfiguration = portalConfigurationData?.[PortalConfigurationType.Inventory];

      if (portalConfiguration) {
        return (
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Inventory,
            PortalConfigurationRolesSettingsKeys.AssignedOnly
          ) ||
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Inventory,
            PortalConfigurationRolesSettingsKeys.ReadOnly
          )
        );
      }
    }

    return false;
  };

  const checkIfLocationsVisible = (
    userProfile: UserProfile,
    vendorProfile: VendorProfile,
    isAftermarketPartner: boolean
  ) => {
    if (isAftermarketPartner) {
      return false;
    }
    return !!vendorProfile?.multiLocationManagement && !byUserRole(userProfile, VendorContactRole.SalesRep);
  };

  const checkIfPrescreenVisible = (
    isDealerUser: boolean,
    isLenderUser: boolean,
    isCommonPartner: boolean,
    isAftermarketPartner: boolean,
    isUserHaveAccessToPrescreenPage: boolean
  ) => {
    if (isCommonPartner || isAftermarketPartner) {
      return false;
    }
    if (isDealerUser) {
      return isUserHaveAccessToPrescreenPage;
    }
    if (isLenderUser) {
      const allowSyndication =
        portalConfigurationData?.[PortalConfigurationType.Portal]?.jsonDefinition?.config?.allowSyndication;

      return isUserHaveAccessToPrescreenPage && allowSyndication;
    }
  };

  const checkIfCustomersVisible = (isDealerUser: boolean) => {
    if (isDealerUser) {
      return !checkPortalConfigEntryForCurrentUserRole(
        PortalConfigurationType.Customers,
        PortalConfigurationRolesSettingsKeys.Hidden
      );
    }

    return false;
  };

  return {
    byUserRole,
    checkIfLocationsVisible,
    checkIfOpportunitiesVisible,
    checkIfAftermarketVisible,
    checkIfPrescreenVisible,
    checkIfSubmissionsHubIsVisible,
    checkIfCustomersVisible,
    checkIfInventoryIsVisible,
  };
}
